import { useState, useEffect } from "react";
import TestsService from '../../services/TestsService';
import {useUser} from '../UserContext';

export default function ExportModal (props) {
    const [tests, setTests] = useState();
    const { user } = useUser();
    const [mounted, setMounted] = useState(false);
    const [selectedTestId, setSelectedTestId] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [message, setMessage] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            const ret = await TestsService.getTests(user.accessToken.token);
            setTests(sortByName(ret));
            setMounted(true);
        };
        if (props.open) {
            fetchData();
            setIsExporting(false); // Reset exporting state when modal opens
            setStartDate('');
            setEndDate('');
            setMessage(null);
        }
    }, [props.open]);
    
    const sortByName = (stringTests) => {      
        return stringTests.sort(function(a,b) {
            if (a.Name.toLowerCase() > b.Name.toLowerCase()) {    
                return 1;    
            } else if (a.Name.toLowerCase() < b.Name.toLowerCase()) {    
                return -1;    
            }    
            return 0;    
        });
    };    

    const handleExport = async () => {
        // Validate date range if both dates are provided
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            setMessage({ type: 'error', text: 'Start date must be before or equal to end date.' });
            return;
        }

        setIsExporting(true);
        setMessage(null);
        
        try {
            const result = await props.onExport(selectedTestId, startDate, endDate);
            if (result && result.success) {
                // Close modal only after successful export
                props.setOpen(false);
                setSelectedTestId('');
                setStartDate('');
                setEndDate('');
                setMessage(null);
            } else {
                // Show error message in modal
                setMessage({ 
                    type: 'error', 
                    text: result.message || 'Export failed. Please try again.' 
                });
            }
        } catch (error) {
            console.error('Export failed:', error);
            setMessage({ 
                type: 'error', 
                text: 'Export failed. Please try again.' 
            });
        } finally {
            setIsExporting(false);
        }
    }
    
    if (props.open && mounted) 
    return (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                                <svg className="h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 className="text-lg leading-6 font-bold text-gray-900" id="modal-title">
                                    Export Candidate Results
                                </h3>
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Test
                                    </label>
                                    <select 
                                        className='w-full p-2 border border-gray-300 rounded-md' 
                                        value={selectedTestId}
                                        onChange={(e) => setSelectedTestId(e.target.value)}
                                    >
                                        <option value=''>Select a test</option>
                                        {tests && tests.map((test) => (
                                            <option key={test.TestId} value={test.TestId}>{test.Name}</option>
                                        ))}                                    
                                    </select>
                                    
                                    <div className="mt-4 grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Start Date (optional)
                                            </label>
                                            <input 
                                                type="date"
                                                className="w-full p-2 border border-gray-300 rounded-md"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                End Date (optional)
                                            </label>
                                            <input 
                                                type="date"
                                                className="w-full p-2 border border-gray-300 rounded-md"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    
                                    <p className="text-sm text-gray-500 mt-2">
                                        Export candidates data to Excel file for the selected test. Date range filters by test completion date.
                                    </p>
                                    
                                    {/* Message display */}
                                    {message && (
                                        <div className={`mt-4 p-3 rounded-md ${
                                            message.type === 'error' 
                                                ? 'bg-red-50 border border-red-200 text-red-700' 
                                                : 'bg-green-50 border border-green-200 text-green-700'
                                        }`}>
                                            <p className="text-sm">{message.text}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 text-center">
                        <button 
                            type="button" 
                            onClick={handleExport}
                            disabled={isExporting || !selectedTestId}
                            className={`w-full sm:w-auto inline-flex justify-center font-bold border-2 rounded-md shadow-sm py-2 px-4 focus:outline-none ${
                                isExporting || !selectedTestId
                                    ? 'border-gray-400 bg-gray-400 text-white cursor-not-allowed' 
                                    : 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700'
                            }`}
                        >
                            {isExporting ? 'Exporting...' : 'Export to Excel'}
                        </button>
                        <button 
                            type="button" 
                            onClick={() => {
                                props.setOpen(false); 
                                setSelectedTestId('');
                                setStartDate('');
                                setEndDate('');
                                setMessage(null);
                            }} 
                            disabled={isExporting}
                            className={`mt-3 sm:mt-0 sm:ml-3 w-full sm:w-auto inline-flex justify-center font-bold border-2 border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none ${
                                isExporting ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50'
                            }`}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>    
    )
    else 
    return (<></>)
}